/**
 * DCT-4 — per-matched-doc sufficiency classification.
 *
 * Sufficiency is INDEPENDENT of the relevance score. A 0.85-score doc
 * with `textContent.length === 200` reads "thin" (matched but the doc
 * is too short to carry meaningful evidence). A 0.45-score 12-page PDF
 * reads "adequate". Don't conflate "this doc fits the requirement"
 * (score) with "this doc carries enough signal to drive a quality
 * report" (sufficiency).
 *
 * Thresholds are intentionally conservative — tune in DCT-THRESHOLD-TUNING
 * after a 2-week production soak (see pack header).
 *
 *   strong   — `textContent.length >= 2000` AND `score >= 0.7`
 *   adequate — `textContent.length >= 500`  AND `score >= 0.4`
 *   thin     — matched but underweight (everything else with text)
 *   unknown  — no `textContent` (binary or unprocessed); also covers
 *              "[extraction failed]" sentinel rows from the PDF text
 *              extractor (PE pack).
 */
import type { SiteDocument } from '../../../types/siteRag';

export type DocSufficiency = 'strong' | 'adequate' | 'thin' | 'unknown';

const STRONG_TEXT_THRESHOLD = 2_000;
const STRONG_SCORE_THRESHOLD = 0.7;
const ADEQUATE_TEXT_THRESHOLD = 500;
const ADEQUATE_SCORE_THRESHOLD = 0.4;

export function computeSufficiency(doc: SiteDocument, score: number): DocSufficiency {
  const text = doc.textContent ?? '';
  // PE-pack convention: extraction failure leaves a `[...]` sentinel.
  // Treat as unknown — we don't want to penalise a doc whose bytes were
  // there but the OCR pipeline couldn't read them.
  if (text.length === 0 || text.startsWith('[')) return 'unknown';
  const len = text.length;
  if (len >= STRONG_TEXT_THRESHOLD && score >= STRONG_SCORE_THRESHOLD) return 'strong';
  if (len >= ADEQUATE_TEXT_THRESHOLD && score >= ADEQUATE_SCORE_THRESHOLD) return 'adequate';
  return 'thin';
}

export const SUFFICIENCY_LABEL: Record<DocSufficiency, string> = {
  strong:   'Thick doc match',
  adequate: 'Adequate doc match',
  thin:     'Thin — limited content',
  unknown:  'Unknown — extract failed',
};
